/**
 * Performance Section (P9) — Profile tab read-only metrics card.
 *
 * Strict reuse of canonical resolvers:
 *   • acceptanceRate  → getContractorOfferMetrics (P2)
 *   • on-time, phase timing, QA pass, trend → getContractorPerformanceMetrics (P9)
 *
 * Sample-size guarded — null states render as "Not enough data" badges,
 * never as "0%" (defamation guard, same as P2).
 *
 * Earnings trend = sparkline only (no $ values).
 */

import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { getContractorOfferMetrics } from "@/data/contractor/offerMetrics";
import { getContractorPerformanceMetrics } from "@/data/contractor/contractorPerformance";

function pct(rate: number | null): string {
  return rate == null ? "—" : `${Math.round(rate * 100)}%`;
}

function formatPhaseDuration(ms: number | null): string {
  if (ms == null) return "—";
  const hours = ms / (1000 * 60 * 60);
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function MetricRow({
  label,
  value,
  insufficient,
}: {
  label: string;
  value: string;
  insufficient: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between py-2 border-b border-border/40 last:border-0">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      {insufficient ? (
        <Badge variant="outline" className="text-[9px] bg-muted text-muted-foreground border-border">
          Not enough data
        </Badge>
      ) : (
        <span className="text-sm font-semibold tabular-nums">{value}</span>
      )}
    </div>
  );
}

function Sparkline({ buckets }: { buckets: number[] }) {
  const max = Math.max(1, ...buckets);
  return (
    <div
      data-testid="earnings-trend-sparkline"
      className="flex items-end gap-1 h-8"
      aria-label="Completed jobs per week, last 4 weeks"
    >
      {buckets.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm bg-primary/40"
          style={{ height: `${(v / max) * 100}%`, minHeight: "2px" }}
          data-bucket={i}
          data-value={v}
        />
      ))}
    </div>
  );
}

export function PerformanceSection() {
  const { jobs, marketplaceOffers, specExecutions, systemEvents } = useAppData();
  const { id: contractorId, matchKey } = useCurrentContractor();

  const offerMetrics = useMemo(
    () =>
      contractorId
        ? getContractorOfferMetrics(contractorId, marketplaceOffers, jobs, systemEvents)
        : null,
    [contractorId, marketplaceOffers, jobs, systemEvents],
  );

  const perf = useMemo(
    () =>
      getContractorPerformanceMetrics({
        contractorId,
        matchKey,
        jobs,
        executions: specExecutions,
        offers: marketplaceOffers,
      }),
    [contractorId, matchKey, jobs, specExecutions, marketplaceOffers],
  );

  return (
    <Card data-testid="performance-section">
      <CardContent className="p-4 space-y-1">
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            Performance
          </h3>
        </div>

        <MetricRow
          label="Acceptance rate"
          value={pct(offerMetrics?.acceptanceRate ?? null)}
          insufficient={offerMetrics?.acceptanceRate == null}
        />
        <MetricRow
          label="On-time check-in"
          value={pct(perf.onTimeCheckInRate)}
          insufficient={perf.onTimeCheckInRate == null}
        />
        <MetricRow
          label="Phase submission (median)"
          value={formatPhaseDuration(perf.medianPhaseSubmissionMs)}
          insufficient={perf.medianPhaseSubmissionMs == null}
        />
        <MetricRow
          label="QA pass rate"
          value={pct(perf.qaPassRate)}
          insufficient={perf.qaPassRate == null}
        />

        <div className="pt-3 mt-1 border-t border-border/40">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] text-muted-foreground">
              Completed jobs · last 4 weeks
            </span>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {perf.samples.completedIn30Days} total
            </span>
          </div>
          <Sparkline buckets={perf.earningsTrendBuckets} />
        </div>

        <p className="text-[10px] text-muted-foreground pt-2">
          Read-only. Metrics return “Not enough data” until at least 3 observations.
        </p>
      </CardContent>
    </Card>
  );
}

export default PerformanceSection;
